import React, { useState } from "react";
import { formatBranchMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";

/**
 * Tabular picker with editable per-row quantity. Used by both the
 * Products and Services tabs.
 *
 * State model — preserved 1:1 with the original:
 *   • `qty` is a { [rowId]: number } map keyed by row id.
 *   • Default quantity is 1 for any row the operator hasn't touched.
 *   • Every edit clamps to `Math.max(1, parseInt(...))` so the
 *     inventory check downstream never receives 0 or NaN.
 *
 * The `onAdd(row, qty)` callback is invoked with the CURRENT qty for
 * that row so the parent can build the correct order-line payload
 * (unit_price × quantity) without needing a second lookup.
 */
export default function PickerTable({ rows, branch, cols, onAdd, testidPrefix }) {
  const [qty, setQty] = useState({});
  const getQty = (id) => qty[id] || 1;
  return (
    <div className="border border-border rounded-lg max-h-[420px] overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {cols.map((c) => (<TableHead key={c.header}>{c.header}</TableHead>))}
            <TableHead className="text-right">Price</TableHead>
            <TableHead className="w-24">Qty</TableHead>
            <TableHead className="w-32 text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow><TableCell colSpan={cols.length + 3} className="text-center text-sm text-muted-foreground py-8">No items match.</TableCell></TableRow>
          ) : rows.map((r) => (
            <TableRow key={r.id} data-testid={`${testidPrefix}-row-${r.id}`}>
              {cols.map((c) => (<TableCell key={c.header}>{c.cell(r)}</TableCell>))}
              <TableCell className="text-right">{r.price == null ? <span className="text-xs uppercase tracking-wider text-muted-foreground">Custom</span> : formatBranchMoney(branch, r.price || 0)}</TableCell>
              <TableCell>
                <Input
                  type="number"
                  min={1}
                  value={getQty(r.id)}
                  onChange={(e) => setQty((q) => ({ ...q, [r.id]: Math.max(1, parseInt(e.target.value || "1", 10)) }))}
                  className="w-20"
                  data-testid={`${testidPrefix}-qty-${r.id}`}
                />
              </TableCell>
              <TableCell className="text-right">
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-primary hover:text-primary hover:bg-primary/10 uppercase tracking-wider text-[11px]"
                  onClick={() => onAdd(r, getQty(r.id))}
                  data-testid={`${testidPrefix}-add-${r.id}`}
                >
                  Add to order
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
